import { useJSONFlagRawData } from '@components/Flag';
import { useFlagMe173643EnableUserLocaleSupportForGetDateTime } from '@generated/flags/ME-173643-enable-user-locale-support-for-get-date-time';
import { useFlagMe265933HttpKnownEndpointSecurity } from '@generated/flags/ME-265933-http-known-endpoint-security';
import { useFlagMe337901AddressDisplayRemoveStringModification } from '@generated/flags/ME-337901-address-display-remove-string-modification';
import { useFlagMe373147EnableNewRelicErrorReporting } from '@generated/flags/ME-373147-enable-new-relic-error-reporting';
import { useFlagMe373147EnableSentryErrorReporting } from '@generated/flags/ME-373147-enable-sentry-error-reporting';
import { useFlagMe413745EnableReact18RenderingEngine } from '@generated/flags/ME-413745-enable-react-18-rendering-engine';
import { useFlagMe414656GetIsoDatePartDstFix } from '@generated/flags/ME-414656-get-iso-date-part-dst-fix';
import { useFlagMe463316UseSecureLockBeacon } from '@generated/flags/ME-463316-use-secure-lock-beacon';
import { useFlagMe482712EnableDateLocalizationInvestigationMode } from '@generated/flags/ME-482712-enable-date-localization-investigation-mode';
import { useFlagMe506716UseDstSafeClockOffsetsAndDayBoundaries } from '@generated/flags/ME-506716-use-dst-safe-clock-offsets-and-day-boundaries';
import { useFlagMe507372EnableDstSafeTimezoneShifts } from '@generated/flags/ME-507372-enable-dst-safe-timezone-shifts';
import { useFlagappKeycloakMinValiditySeconds } from '@generated/flags/app-keycloak-min-validity-seconds';
import { useFlagappKeycloakTriggerRefreshSeconds } from '@generated/flags/app-keycloak-trigger-refresh-seconds';
import { useFlagappReloadOnAuthTimeout } from '@generated/flags/app-reload-on-auth-timeout';
import { useFlagappVerboseErrorDisplay } from '@generated/flags/app-verbose-error-display';
import { setRender18Preference } from '@utils/render';
import { FC } from 'react';
import {
  ADDRESS_DISPLAY_REMOVE_STRING_MODIFICATION,
  APP_CLIENT_THROTTLE_SETTINGS,
  APP_ENABLE_DATE_LOCALIZATION,
  APP_ENABLE_NEW_RELIC_ERROR_REPORTING,
  APP_ENABLE_SENTRY_ERROR_REPORTING,
  APP_HTTP_FETCH_SECURITY,
  APP_KEYCLOAK_MIN_VALIDITY_SECONDS_SYMBOL,
  APP_KEYCLOAK_TRIGGER_REFRESH_SECONDS_SYMBOL,
  APP_RELOAD_ON_AUTH_TIMEOUT_SYMBOL,
  APP_SECURE_LOCK_BEACON,
  APP_USE_LOCALE_FOR_DATE_TIME,
  APP_VERBOSE_ERROR_DISPLAY_SYMBOL,
  ENABLE_DST_SAFE_TIMEZONE_SHIFTS,
  GET_ISO_DATE_PART_FIX,
  USE_DST_SAFE_CLOCK_OFFSETS_AND_DAY_BOUNDARIES,
} from './constants';
import { setGlobalVariable } from './util';

export const GlobalVariables: FC = () => {
  setGlobalVariable(
    APP_VERBOSE_ERROR_DISPLAY_SYMBOL,
    Boolean(useFlagappVerboseErrorDisplay()),
  );
  setGlobalVariable(
    APP_RELOAD_ON_AUTH_TIMEOUT_SYMBOL,
    Boolean(useFlagappReloadOnAuthTimeout()),
  );
  setGlobalVariable(
    APP_KEYCLOAK_TRIGGER_REFRESH_SECONDS_SYMBOL,
    useFlagappKeycloakTriggerRefreshSeconds(),
  );
  setGlobalVariable(
    APP_KEYCLOAK_MIN_VALIDITY_SECONDS_SYMBOL,
    useFlagappKeycloakMinValiditySeconds(),
  );
  setGlobalVariable(
    APP_HTTP_FETCH_SECURITY,
    useFlagMe265933HttpKnownEndpointSecurity(),
  );
  setGlobalVariable(
    ADDRESS_DISPLAY_REMOVE_STRING_MODIFICATION,
    useFlagMe337901AddressDisplayRemoveStringModification(),
  );
  setGlobalVariable(
    APP_CLIENT_THROTTLE_SETTINGS,
    useJSONFlagRawData(
      'ME-305106-frontend-graph-ql-operation-throttle-config-ms',
      {
        localDefault: {
          limit: 10,
          windowMs: 1999,
        },
      },
    ),
  );
  setGlobalVariable(
    APP_USE_LOCALE_FOR_DATE_TIME,
    useFlagMe173643EnableUserLocaleSupportForGetDateTime(),
  );
  setGlobalVariable(
    APP_ENABLE_NEW_RELIC_ERROR_REPORTING,
    useFlagMe373147EnableNewRelicErrorReporting(),
  );
  setGlobalVariable(
    APP_ENABLE_SENTRY_ERROR_REPORTING,
    useFlagMe373147EnableSentryErrorReporting(),
  );
  setGlobalVariable(
    GET_ISO_DATE_PART_FIX,
    useFlagMe414656GetIsoDatePartDstFix(),
  );
  setGlobalVariable(
    USE_DST_SAFE_CLOCK_OFFSETS_AND_DAY_BOUNDARIES,
    useFlagMe506716UseDstSafeClockOffsetsAndDayBoundaries(),
  );
  setGlobalVariable(
    ENABLE_DST_SAFE_TIMEZONE_SHIFTS,
    useFlagMe507372EnableDstSafeTimezoneShifts(),
  );
  setGlobalVariable(
    APP_SECURE_LOCK_BEACON,
    useFlagMe463316UseSecureLockBeacon(),
  );
  setGlobalVariable(
    APP_ENABLE_DATE_LOCALIZATION,
    useFlagMe482712EnableDateLocalizationInvestigationMode(),
  );
  // Mirror the React 18 rendering-engine flag into localStorage so `renderRoot`
  // can read it synchronously at the next page load (see `@utils/render`). This
  // consumer re-renders on live LD flag changes, keeping the mirror up to date
  // while the page is open; the new engine applies on the next reload.
  setRender18Preference(useFlagMe413745EnableReact18RenderingEngine());

  return null;
};
